import React, { memo, useMemo, useRef, useCallback } from 'react'
import { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheck,
    faEllipsisH,
    faBolt,
    faPause,
    faLock,
    faTimes,
    faExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { Message } from 'primereact/message'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Link } from 'react-router-dom'

import { IconSuspendedEv } from '../icon-suspended-ev'
import { getConnectorState, ConnectorState } from '../get-connector-state'
import { Connector, useTroubleshootingGetErrorCodeTroubleshootingInfoQuery } from '../../common/redux/api/web-api'
import { ConnectorChargingInfo } from '../connector-charging-info'
import { getErrorcodeInstructions } from '../../common/utils/errorStatus'
import Text from '../../ui/Text'
import useText from '../../common/hooks/useText'
import { ErrorCode, ErrorCodeInstructions } from '../../common/enums/errorCodeEnum'

export type ConnectorStatusIconColor = 'yellow' | 'blue' | 'green' | 'red' | 'gray' | 'darkGreen'

export const SConnectorStatusIcon = styled.span<{ color?: ConnectorStatusIconColor; center?: boolean }>`
    font-size: 1.5em;
    color: ${({ color }) => {
        switch (color) {
            case 'yellow':
                return '#ffcc4c'
            case 'green':
                return '#00c400'
            case 'darkGreen':
                return '#006c5c'
            case 'blue':
                return '#2277dd'
            case 'red':
                return '#dd0000'
            case 'gray':
                return '#888888'
            default:
                return '#000000'
        }
    }};
`

function ConnectorStatus({
    connector,
    isOcpp,
    labeled,
    showErrorStatus,
    showConsumption,
    productId,
}: ConnectorStatusProps): React.ReactElement {
    const text = useText()
    const state = useMemo(() => connector && getConnectorState(connector, isOcpp), [connector, isOcpp])
    const toggleOverlay = useRef<OverlayPanel>(null)

    const getErrorcode = useCallback(
        (externalError?: string | null, errorCode?: string | null) => {
            switch (externalError) {
                case ErrorCode.ConnectorLockFailure:
                    return text('errorConnectorLockFailure')

                case ErrorCode.GroundFailure:
                    return text('errorGroundFailure')

                case ErrorCode.HighTemperature:
                    return text('errorHighTemperature')

                case ErrorCode.OverCurrentFailure:
                    return text('errorOverCurrentFailure')

                case ErrorCode.PowerMeterFailure:
                    return text('errorPowerMeterFailure')

                case ErrorCode.UnderVoltage:
                    return text('errorUnderVoltage')

                case ErrorCode.EvSignalingError:
                    return text('errorEvSignalingError')

                case ErrorCode.InternalError:
                    return text('errorInternalError')

                default:
                    return (externalError ? externalError : errorCode) ?? undefined
            }
        },
        [text]
    )

    const { data: errorCodeInfo } = useTroubleshootingGetErrorCodeTroubleshootingInfoQuery(
        {
            productId: productId,
            errorCode: connector?.errorCode ?? '',
        },
        { skip: !isOcpp || connector?.errorCode === 'NoError' }
    )

    const showErrorStatusIcon = connector?.errorCode !== 'NoError' && !showErrorStatus && errorCodeInfo

    const showErrorInfo = showErrorStatus && connector?.errorCode !== 'NoError' && errorCodeInfo

    const userInstructions = errorCodeInfo?.instructions?.map((instruction) => {
        return (
            <span className="flex flex-column gap-4 p-1 pl-2">
                {getErrorcodeInstructions(instruction as ErrorCodeInstructions)}
            </span>
        )
    })

    const icon = useMemo(() => {
        switch (state) {
            case ConnectorState.AVAILABLE:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faCheck}
                            color="#23c421"
                            title={text('lblConnectorAvailableStatus')}
                            data-testid={`status-available-${connector?.name}`}
                            aria-labelledby="color"
                            titleId="icon"
                            className="mt-1 pointer"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo.externalErrorCode, connector?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PREPARING:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            color="#23c421"
                            title={text('lblConnectorPreparingStatus')}
                            data-testid="status-preparing"
                            aria-labelledby="color"
                            titleId="icon"
                            className="pointer"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.CONNECTED:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            color="#23c421"
                            title={text('lblConnectorConnectedToEVStatus')}
                            data-testid="status-connected"
                            aria-labelledby="color"
                            titleId="icon"
                            className="pointer"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.CHARGING:
                return showConsumption ? (
                    <ConnectorChargingInfo connector={connector} />
                ) : (
                    <>
                        <FontAwesomeIcon
                            icon={faBolt}
                            color="#2277dd"
                            title={text('lblConnectorChargingStatus')}
                            data-testid="status-charging"
                            className="mt-1 pointer"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PAUSED_BY_CHARGER:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faPause}
                            className="pointer"
                            color="#2277dd"
                            title={text('lblConnectorPausedByChargerStatus')}
                            data-testid="pausedByCharger-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PAUSED_BY_VEHICLE:
                return (
                    <span title={text('lblConnectorPausedByVehicleStatus')}>
                        <IconSuspendedEv width={23} height={12} />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </span>
                )
            case ConnectorState.FINISHING:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            className="pointer"
                            color="#888888"
                            title={text('lblConnectorFinishingStatus')}
                            data-testid="finishing-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.RESERVED:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faLock}
                            className="pointer"
                            color="#888888"
                            title={text('lblConnectorReservedStatus')}
                            data-testid="reserved-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.UNAVAILABLE:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="pointer"
                            color="#888888"
                            title={text('lblConnectorNotAvailableStatus')}
                            data-testid="unavailable-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.FAULTED:
                return (
                    !showErrorStatus && (
                        <FontAwesomeIcon
                            icon={faExclamation}
                            color="#dd2222"
                            title={
                                connector?.errorCode && !showErrorStatus
                                    ? getErrorcode(connector?.errorCode)
                                    : text('lblConnectorErrorStatus')
                            }
                            data-testid="faulted-status"
                            className="pointer"
                        />
                    )
                )

            default:
                return (
                    !showErrorStatus && (
                        <FontAwesomeIcon
                            icon={faExclamation}
                            className="pointer"
                            color="#dd2222"
                            title={
                                connector?.errorCode && !showErrorStatus
                                    ? getErrorcode(connector?.errorCode)
                                    : text('lblConnectorErrorStatus')
                            }
                            data-testid="fa-exclamation"
                        />
                    )
                )
        }
    }, [state, text, connector, showErrorStatusIcon, getErrorcode, errorCodeInfo, showConsumption, showErrorStatus])

    const label = useMemo(() => (labeled ? state : undefined), [state, labeled])

    return (
        <>
            <div className="flex flex-column align-items-center gap-2 w-12 text-sm sm:flex-row">
                {icon}

                {showErrorInfo && (
                    <>
                        <Message
                            data-testid="error-status-message"
                            severity={connector?.ocppStatus === 'Faulted' && connector?.errorCode ? 'error' : 'warn'}
                            content={
                                <>
                                    <i className="pi pi-exclamation-triangle mr-1"></i>
                                    {getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}

                                    <i
                                        className="custom-target-icon-error pi pi-question-circle ml-1 pointer"
                                        data-pr-classname="col-3"
                                        onMouseEnter={(e) => toggleOverlay.current?.toggle(e)}
                                    ></i>
                                </>
                            }
                            className="flex align-items-center text-xs p-2 ml-1 sm: text-sm"
                        />
                    </>
                )}
            </div>
            {label && <div className="mt-1"> {label}</div>}

            <OverlayPanel ref={toggleOverlay} className="border-400 mr-5 sm:max-w-17rem">
                {userInstructions && userInstructions.length > 0 ? (
                    userInstructions
                ) : (
                    <span className="ml-2 mr-2 flex align-items-center gap-1">
                        <i className="pi pi-whatsapp"></i>
                        <Text code="lbllinkSupport" />

                        <Link
                            to={`https://support-se.zendesk.com/hc/${text('linkHelpCenter')}`}
                            target="_blank"
                            data-testid="go-to-support-page"
                            className="pi pi-external-link text-xs mt-1"
                        ></Link>
                    </span>
                )}
            </OverlayPanel>
        </>
    )
}

export type ConnectorStatusProps = {
    connector: Connector | undefined
    errorCode?: string
    isOcpp: boolean | undefined
    labeled?: boolean | undefined
    className?: ClassName
    style?: React.CSSProperties
    showErrorStatus?: boolean
    productId?: number
    showConsumption?: boolean
}

export default memo(ConnectorStatus)
